import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Table, Column } from '@/shared/ui/Table';
import { Badge } from '@/shared/ui/Badge';
import { AdCampaign } from '../api/adsApi';
import { formatConversions, formatCurrency } from '@/shared/utils/format';
import styles from './CampaignsTable.module.css';

interface CampaignsTableProps {
  campaigns: AdCampaign[];
  loading?: boolean;
  /** When set, campaign links carry a "back to <label>" target (this page + its
   *  current query, e.g. the selected date range) for the detail page. */
  backLabel?: string;
}

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'neutral' | 'error'> = {
  ACTIVE: 'success',
  PAUSED: 'neutral',
  ENDED: 'neutral',
  PENDING: 'warning',
};

export function CampaignsTable({ campaigns, loading, backLabel }: CampaignsTableProps) {
  const location = useLocation();
  const backState = backLabel
    ? { from: location.pathname + location.search, fromLabel: backLabel }
    : undefined;

  // Pin widths on non-flex columns so Google + Meta tables align even though
  // they're rendered as independent <table> elements.
  const columns: Column<AdCampaign>[] = [
    {
      key: 'name',
      header: 'Campaign',
      render: (row) =>
        row.marketingCampaignId ? (
          <Link
            to={`/campaigns/${row.marketingCampaignId}`}
            state={backState}
            className={styles.campaignNameLink}
          >
            {row.name}
          </Link>
        ) : (
          <span className={styles.campaignName}>{row.name}</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      render: (row) => (
        <Badge variant={STATUS_VARIANTS[row.status.toUpperCase()] ?? 'neutral'}>{row.status}</Badge>
      ),
    },
    {
      key: 'spend',
      header: 'Spend',
      width: '140px',
      render: (row) => formatCurrency(row.spend),
      align: 'right',
    },
    {
      key: 'conversions',
      header: 'Conv.',
      width: '100px',
      render: (row) => formatConversions(row.conversions),
      align: 'right',
    },
    {
      key: 'cpa',
      header: 'CPA',
      width: '120px',
      render: (row) => {
        if (row.cpa) return formatCurrency(row.cpa);
        if (row.spend > 0 && row.conversions > 0)
          return formatCurrency(row.spend / row.conversions);
        return '—';
      },
      align: 'right',
    },
  ];

  return (
    <Table
      columns={columns}
      data={campaigns}
      loading={loading}
      emptyMessage="No campaigns found"
      rowClassName={(row) => (row.status.toUpperCase() === 'PAUSED' ? styles.rowPaused : undefined)}
    />
  );
}
